import type { ChangeEvent, DragEvent, MouseEvent as ReactMouseEvent, PointerEvent, } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { PreviewHistoryItem, PreviewSeed, Ratio, UploadAssetSpec, } from "@contracts/types";
import { loadDraft, saveDraft, type StoredDraft } from "../lib/draft-storage";
import { formatIdr, useLinkPricing } from "../lib/pricing";
import { SiteSupport } from "./SiteSupport";
import { permanentSample } from "../lib/sample";
type AccountProfile = {
    id: string;
    email: string;
    name: string;
    avatarUrl?: string;
};
type PaymentNotice = {
    tone: "pending" | "success" | "error";
    message: string;
} | null;
import { CAPTION_PREVIEW_CHARS, MAX_CAPTION_CHARS, MAX_IMAGE_BYTES, MAX_SLIDES, MAX_TOTAL_BYTES, MAX_VIDEO_BYTES, MAX_VIDEO_SECONDS, } from "@contracts/types";
type UploadedSlide = {
    id: string;
    kind: "image" | "video";
    url: string;
    name: string;
    file?: File;
    duration?: number;
    sourceRatio?: Ratio;
};
type Slide = UploadedSlide;
const ratios: {
    value: Ratio;
    label: string;
    dimensions: string;
}[] = [
    { value: "4 / 5", label: "4:5 Portrait", dimensions: "1080 × 1350" },
    { value: "3 / 4", label: "3:4 Portrait", dimensions: "1080 × 1440" },
    { value: "1 / 1", label: "1:1 Square", dimensions: "1080 × 1080" },
    { value: "1.91 / 1", label: "1.91:1 Landscape", dimensions: "1080 × 566" },
    { value: "9 / 16", label: "9:16 Story / Reel", dimensions: "1080 × 1920" },
];
const DEFAULT_USERNAME = permanentSample.username;
const DEFAULT_LOCATION = permanentSample.location;
const DEFAULT_LIKES = permanentSample.likes;
const DEFAULT_CAPTION = permanentSample.caption;
const DEFAULT_AVATAR: string | null = null;
const EDITOR_KEY_STORAGE = "pawlo-carousel-editor-key";
const TERMS_VERSION = "2026-10-27";
const TERMS_STORAGE = "rupapreview-terms-version";
type UploadAsset = UploadAssetSpec & {
    file: File;
    slot: string;
};
async function readApiResponse<T>(response: Response): Promise<T> {
    const raw = await response.text();
    let parsed: {
        error?: string;
    } & Partial<T> = {};
    if (raw) {
        try {
            parsed = JSON.parse(raw) as {
                error?: string;
            } & Partial<T>;
        }
        catch {
            parsed = {};
        }
    }
    if (!response.ok) {
        const fallback = response.status === 413
            ? "The upload is too large for the server. Please reduce the file size and try again."
            : `The preview could not be created (error ${response.status}).`;
        throw new Error(parsed.error || fallback);
    }
    return parsed as T;
}
async function fetchPreviewHistory(editorKey?: string) {
    const response = await fetch("/api/share/history", {
        headers: editorKey ? { "x-editor-key": editorKey } : undefined,
        cache: "no-store",
    });
    return readApiResponse<{
        items: PreviewHistoryItem[];
    }>(response);
}
function formatPreviewDate(value: string) {
    const normalized = value.includes("T")
        ? value
        : `${value.replace(" ", "T")}Z`;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime()))
        return value;
    return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Asia/Jakarta",
        timeZoneName: "short",
    }).format(date);
}
function formatLikeCount(value: string, liked: boolean) {
    if (!liked)
        return value || "0";
    const count = Number((value || "0").replaceAll(",", ""));
    if (!Number.isFinite(count))
        return value || "0";
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 20 }).format(count + 1);
}
function describeAsset(file: File, slot: string, kind: "image" | "video", duration?: number, sourceRatio?: Ratio): UploadAsset {
    return {
        file,
        slot,
        kind,
        name: file.name || slot,
        type: file.type,
        size: file.size,
        ...(kind === "video" && duration ? { duration } : {}),
        ...(sourceRatio ? { sourceRatio } : {}),
    };
}
function getFileKind(file: File): "image" | "video" | null {
    const extension = file.name.toLowerCase().split(".").pop() ?? "";
    const type = file.type.toLowerCase();
    if (["jpg", "jpeg", "png"].includes(extension) &&
        (!type || ["image/jpeg", "image/png"].includes(type)))
        return "image";
    if (["mp4", "mov"].includes(extension) &&
        (!type || ["video/mp4", "video/quicktime"].includes(type)))
        return "video";
    return null;
}
function nearestRatio(width: number, height: number): Ratio {
    const actual = width / height;
    return ratios.reduce((closest, item) => {
        const [widthValue, heightValue] = item.value.split(" / ").map(Number);
        const [closestWidth, closestHeight] = closest.value
            .split(" / ")
            .map(Number);
        return Math.abs(actual - widthValue / heightValue) <
            Math.abs(actual - closestWidth / closestHeight)
            ? item
            : closest;
    }, ratios[0]).value;
}
function inspectMedia(url: string, kind: "image" | "video") {
    return new Promise<{
        sourceRatio: Ratio;
        duration?: number;
    }>((resolve, reject) => {
        const timeout = window.setTimeout(() => reject(new Error("metadata timed out")), 20000);
        const finish = (width: number, height: number, duration?: number) => {
            window.clearTimeout(timeout);
            if (!width || !height) {
                reject(new Error("dimensions are unavailable"));
                return;
            }
            resolve({
                sourceRatio: nearestRatio(width, height),
                ...(duration !== undefined ? { duration } : {}),
            });
        };
        if (kind === "image") {
            const image = new Image();
            image.onload = () => finish(image.naturalWidth, image.naturalHeight);
            image.onerror = () => {
                window.clearTimeout(timeout);
                reject(new Error("image metadata could not be read"));
            };
            image.src = url;
            return;
        }
        const video = document.createElement("video");
        video.preload = "metadata";
        video.muted = true;
        video.playsInline = true;
        video.onloadedmetadata = () => finish(video.videoWidth, video.videoHeight, video.duration);
        video.onerror = () => {
            window.clearTimeout(timeout);
            reject(new Error("video metadata could not be read"));
        };
        video.src = url;
    });
}
function ProfilePlaceholder() {
    return (<svg className="profile-placeholder" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="8" r="3.5"/>
      <path d="M5 20c.7-4 3.1-6 7-6s6.3 2 7 6"/>
    </svg>);
}
function SlideVisual({ slide, thumbnail = false, }: {
    slide: Slide;
    thumbnail?: boolean;
}) {
    if (slide.kind === "image") {
        return <img src={slide.url} alt={slide.name} draggable={false}/>;
    }
    return (<video src={slide.url} controls={!thumbnail} muted={thumbnail} playsInline aria-label={slide.name}/>);
}
function Icon({ name, filled = false, }: {
    name: "heart" | "comment" | "repost" | "share" | "save" | "more";
    filled?: boolean;
}) {
    if (filled && (name === "heart" || name === "save")) {
        return (<svg className="ig-solid-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d={name === "heart"
                ? "M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z"
                : "M6 3h12a2 2 0 0 1 2 2v16l-8-5-8 5V5a2 2 0 0 1 2-2Z"}/>
      </svg>);
    }
    if (name === "repost") {
        return (<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="m17 2 4 4-4 4"/>
        <path d="M3 11V9a3 3 0 0 1 3-3h15"/>
        <path d="m7 22-4-4 4-4"/>
        <path d="M21 13v2a3 3 0 0 1-3 3H3"/>
      </svg>);
    }
    return <span className={`ig-icon ig-icon--${name}`} aria-hidden="true"/>;
}
function ZoomIcon() {
    return <span className="ig-icon ig-icon--search" aria-hidden="true"/>;
}
export function CarouselApp({ initialPreview, demoPreview, sampleMode = false, }: {
    initialPreview?: PreviewSeed;
    demoPreview?: PreviewSeed;
    sampleMode?: boolean;
}) {
    const readOnly = Boolean(initialPreview);
    const seed = initialPreview ?? demoPreview;
    const seededSlides: Slide[] = seed
        ? seed.slides.map((slide, index) => ({
            ...slide,
            id: `shared-${index}`,
        }))
        : [];
    const [slides, setSlides] = useState<Slide[]>(seededSlides);
    const [active, setActive] = useState(0);
    const [ratio, setRatio] = useState<Ratio>(seed?.ratio ?? "4 / 5");
    const [username, setUsername] = useState(seed?.username ?? DEFAULT_USERNAME);
    const [location, setLocation] = useState(seed?.location ?? DEFAULT_LOCATION);
    const [caption, setCaption] = useState(seed?.caption ?? DEFAULT_CAPTION);
    const [likes, setLikes] = useState(seed?.likes ?? DEFAULT_LIKES);
    const [avatar, setAvatar] = useState<string | null>(seed?.avatarUrl ?? DEFAULT_AVATAR);
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [captionOpen, setCaptionOpen] = useState(false);
    const presentMode = readOnly;
    const [draggedId, setDraggedId] = useState<string | null>(null);
    const [shareStatus, setShareStatus] = useState<"idle" | "saving" | "ready">("idle");
    const [shareProgress, setShareProgress] = useState("");
    const [shareUrl, setShareUrl] = useState("");
    const [shareError, setShareError] = useState("");
    const [editorKey, setEditorKey] = useState("");
    const [previewHistory, setPreviewHistory] = useState<PreviewHistoryItem[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState("");
    const [restoringId, setRestoringId] = useState("");
    const [deletingId, setDeletingId] = useState("");
    const [copiedHistoryId, setCopiedHistoryId] = useState("");
    const [zoomedSlide, setZoomedSlide] = useState<Slide | null>(null);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [liked, setLiked] = useState(false);
    const [reposted, setReposted] = useState(false);
    const [saved, setSaved] = useState(false);
    const [simulatorNotice, setSimulatorNotice] = useState("");
    const [uploadError, setUploadError] = useState("");
    const [uploadChecking, setUploadChecking] = useState(false);
    const [account, setAccount] = useState<AccountProfile | null>(null);
    const linkPrice = useLinkPricing();
    const linkPriceLabel = formatIdr(linkPrice);
    const [accountLoading, setAccountLoading] = useState(!readOnly);
    const [paymentNotice, setPaymentNotice] = useState<PaymentNotice>(null);
    const [checkoutReadiness, setCheckoutReadiness] = useState<{ available: boolean; mode: string | null } | null>(null);
    const [termsAccepted, setTermsAccepted] = useState(() => {
        try {
            return window.localStorage.getItem(TERMS_STORAGE) === TERMS_VERSION;
        }
        catch {
            return false;
        }
    });
    const [draftReady, setDraftReady] = useState(readOnly);
    const uploadRef = useRef<HTMLInputElement>(null);
    const avatarRef = useRef<HTMLInputElement>(null);
    const pointerStart = useRef<number | null>(null);
    const suppressZoom = useRef(false);
    useEffect(() => {
        if (readOnly) return;
        let active = true;
        fetch("/api/payments/readiness", { cache: "no-store" })
            .then(async response => response.ok ? response.json() : { available: false, mode: null })
            .then(result => { if (active) setCheckoutReadiness(result); })
            .catch(() => { if (active) setCheckoutReadiness({ available: false, mode: null }); });
        return () => { active = false; };
    }, [readOnly]);
    useEffect(() => {
        if (readOnly)
            return;
        let active = true;
        loadDraft()
            .then((draft) => {
            if (!active)
                return;
            // Replace only the untouched placeholder saved by older releases.
            // Customer media, custom wording and deliberately cleared drafts stay intact.
            const legacyPlaceholder = draft && !draft.slides.length && !draft.avatarFile &&
                draft.username === "Jane Doe" && draft.location === "Anytown, USA" &&
                draft.caption.startsWith("Lorem ipsum dolor sit amet,");
            if (draft && !legacyPlaceholder) {
                setSlides(draft.slides.map((slide) => ({
                    ...slide,
                    url: slide.file ? URL.createObjectURL(slide.file) : slide.url ?? "",
                })));
                setActive(Math.min(draft.active, Math.max(draft.slides.length - 1, 0)));
                setRatio(draft.ratio);
                setUsername(draft.username);
                setLocation(draft.location);
                setCaption(draft.caption);
                setLikes(draft.likes);
                setAvatarFile(draft.avatarFile);
                setAvatar(draft.avatarFile ? URL.createObjectURL(draft.avatarFile) : draft.avatarUrl ?? null);
                setCaptionOpen(draft.captionOpen);
                setLiked(draft.liked);
                setReposted(draft.reposted);
                setSaved(draft.saved);
            }
        })
            .catch(() => {
            if (active)
                setUploadError("Your previous draft could not be restored. You can still start a new one.");
        })
            .finally(() => {
            if (active)
                setDraftReady(true);
        });
        return () => {
            active = false;
        };
    }, [readOnly]);
    useEffect(() => {
        if (readOnly || !draftReady)
            return;
        const timeout = window.setTimeout(() => {
            void persistDraft().catch(() => undefined);
        }, 250);
        return () => window.clearTimeout(timeout);
    }, [
        readOnly,
        draftReady,
        slides,
        active,
        ratio,
        username,
        location,
        caption,
        likes,
        avatarFile,
        captionOpen,
        liked,
        reposted,
        saved,
    ]);
    function currentDraft(): StoredDraft {
        return {
            slides: slides.map((slide) => ({
                id: slide.id,
                kind: slide.kind,
                name: slide.name,
                ...(slide.file ? { file: slide.file } : { url: slide.url }),
                ...(slide.duration !== undefined
                    ? { duration: slide.duration }
                    : {}),
                ...(slide.sourceRatio
                    ? { sourceRatio: slide.sourceRatio }
                    : {}),
            })),
            active,
            ratio,
            username,
            location,
            caption,
            likes,
            avatarFile,
            avatarUrl: avatarFile ? null : avatar,
            captionOpen,
            liked,
            reposted,
            saved,
        };
    }
    async function persistDraft() {
        if (!readOnly)
            await saveDraft(currentDraft());
    }
    useEffect(() => {
        if (readOnly)
            return;
        const keyFromUrl = new URLSearchParams(window.location.search).get("edit") ?? "";
        if (keyFromUrl) {
            try {
                window.localStorage.setItem(EDITOR_KEY_STORAGE, keyFromUrl);
            }
            catch {
                // The URL remains the fallback when browser storage is unavailable.
            }
        }
        let rememberedKey = "";
        try {
            rememberedKey = window.localStorage.getItem(EDITOR_KEY_STORAGE) ?? "";
        }
        catch {
            rememberedKey = "";
        }
        const key = keyFromUrl || rememberedKey;
        if (!key)
            return;
        Promise.resolve()
            .then(() => {
            setEditorKey(key);
            setHistoryLoading(true);
            return fetchPreviewHistory(key);
        })
            .then((result) => setPreviewHistory(result.items ?? []))
            .catch((error) => setHistoryError(error instanceof Error
            ? error.message
            : "Saved previews could not be loaded."))
            .finally(() => setHistoryLoading(false));
    }, [readOnly]);
    async function refreshAccount() {
        const response = await fetch("/api/me", { cache: "no-store" });
        if (response.status === 401) {
            setAccount(null);
            return null;
        }
        const profile = await readApiResponse<AccountProfile | {
            authenticated: false;
        }>(response);
        if (!("id" in profile)) {
            setAccount(null);
            return null;
        }
        setAccount(profile);
        return profile;
    }
    useEffect(() => {
        if (readOnly || !draftReady)
            return;
        if (editorKey) {
            setAccountLoading(false);
            return;
        }
        let active = true;
        setAccountLoading(true);
        const clearPaymentQuery = (query: URLSearchParams) => {
            query.delete("payment");
            query.delete("paymentId");
            query.delete("previewId");
            const suffix = query.toString();
            window.history.replaceState({}, "", `${window.location.pathname}${suffix ? `?${suffix}` : ""}`);
        };
        const loadAccountAndPayment = async () => {
            try {
                await refreshAccount();
                const query = new URLSearchParams(window.location.search);
                const paymentId = query.get("paymentId") || "";
                const previewId = query.get("previewId") || "";
                const paymentState = query.get("payment");
                if (!paymentId || !previewId || !paymentState)
                    return;
                if (paymentState === "cancelled") {
                    setPaymentNotice({
                        tone: "error",
                        message: "QR checkout was cancelled. Your draft is preserved and no preview link was activated.",
                    });
                    clearPaymentQuery(query);
                    return;
                }
                setPaymentNotice({
                    tone: "pending",
                    message: "Checking your QR payment. The preview link activates only after verification.",
                });
                let paid = false;
                let paidAmount: number | null = null;
                for (let attempt = 0; attempt < 10 && active; attempt += 1) {
                    const response = await fetch(`/api/payments/status?id=${encodeURIComponent(paymentId)}`, { cache: "no-store" });
                    if (response.ok) {
                        const payment = (await response.json()) as {
                            status: string;
                            shareUploadId: string;
                            amount: number;
                            paid: boolean;
                        };
                        if (payment.paid && payment.shareUploadId === previewId) {
                            paid = true;
                            paidAmount = payment.amount;
                            break;
                        }
                        if (["failed", "expired"].includes(payment.status)) {
                            setPaymentNotice({
                                tone: "error",
                                message: "This QR payment was not completed. Your draft remains available for a new attempt.",
                            });
                            clearPaymentQuery(query);
                            return;
                        }
                    }
                    if (attempt < 9)
                        await new Promise((resolve) => window.setTimeout(resolve, 2000));
                }
                if (!active)
                    return;
                if (!paid) {
                    setPaymentNotice({
                        tone: "pending",
                        message: "Payment is still pending. Refresh this page shortly; your draft and order are preserved.",
                    });
                    return;
                }
                await finishExistingSharePreview(previewId, paymentId);
                if (!active)
                    return;
                setPaymentNotice({
                    tone: "success",
                    message: paidAmount
                        ? `Payment verified. Your ${formatIdr(paidAmount)} preview link is ready below.`
                        : "Payment verified. Your preview link is ready below.",
                });
                clearPaymentQuery(query);
            }
            catch (error) {
                if (active) {
                    setShareStatus("idle");
                    setShareProgress("");
                    setPaymentNotice({
                        tone: "error",
                        message: error instanceof Error
                            ? `${error.message} Refresh to retry this paid upload.`
                            : "We could not finish the paid upload. Refresh to retry.",
                    });
                }
            }
            finally {
                if (active)
                    setAccountLoading(false);
            }
        };
        void loadAccountAndPayment();
        return () => {
            active = false;
        };
    }, [readOnly, editorKey, draftReady]);
    useEffect(() => {
        if (readOnly || editorKey || !account)
            return;
        setHistoryLoading(true);
        setHistoryError("");
        fetchPreviewHistory()
            .then((result) => setPreviewHistory(result.items ?? []))
            .catch((error) => setHistoryError(error instanceof Error
            ? error.message
            : "Saved previews could not be loaded."))
            .finally(() => setHistoryLoading(false));
    }, [readOnly, editorKey, account?.id]);
    useEffect(() => {
        if (!zoomedSlide)
            return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape")
                setZoomedSlide(null);
        };
        window.addEventListener("keydown", closeOnEscape);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", closeOnEscape);
        };
    }, [zoomedSlide]);
    const current = slides[active];
    const hasDemoSlides = slides.some((slide) => !slide.file);
    const currentRatio = ratios.find((item) => item.value === ratio) ?? ratios[0];
    const displayedLikes = formatLikeCount(likes, liked);
    const truncatedCaption = useMemo(() => {
        if (captionOpen || caption.length <= CAPTION_PREVIEW_CHARS)
            return caption;
        return `${caption.slice(0, CAPTION_PREVIEW_CHARS)}...`;
    }, [caption, captionOpen]);
    useEffect(() => {
        if ((!readOnly || sampleMode) && slides[0]?.sourceRatio) {
            setRatio(slides[0].sourceRatio);
        }
    }, [slides, readOnly, sampleMode]);
    const editorTip = slides.length > 1
        ? "Swipe or use the arrow keys to explore."
        : current?.kind === "video"
            ? "Play the video to review your post."
            : current?.kind === "image"
                ? "Tap the image to zoom and inspect it."
                : "Upload media to start your preview.";
    const publicTip = slides.length > 1
        ? "Swipe, tap, and zoom to check your post."
        : current?.kind === "video"
            ? "Play the video and check how your post looks."
            : "Tap the image to zoom and check the details.";
    function showSimulatorNotice(message: string) {
        setSimulatorNotice(message);
        window.setTimeout(() => {
            setSimulatorNotice((current) => (current === message ? "" : current));
        }, 2200);
    }
    async function shareCurrentPage() {
        try {
            await navigator.clipboard.writeText(window.location.href);
            showSimulatorNotice("Page link copied.");
        }
        catch {
            showSimulatorNotice("Copy the page URL from your browser to share it.");
        }
    }
    function go(direction: number) {
        setActive((index) => {
            const next = index + direction;
            return Math.min(Math.max(next, 0), slides.length - 1);
        });
    }
    async function handleFiles(event: ChangeEvent<HTMLInputElement>) {
        const input = event.target;
        if (uploadChecking) {
            input.value = "";
            return;
        }
        const selected = Array.from(input.files ?? []);
        const room = Math.max(MAX_SLIDES - slides.length, 0);
        const files = selected.slice(0, room);
        const errors = selected.length > room
            ? [`A post can contain up to ${MAX_SLIDES} media items.`]
            : [];
        if (!files.length) {
            setUploadError(errors.join(" "));
            input.value = "";
            return;
        }
        setUploadChecking(true);
        setUploadError("");
        const additions: UploadedSlide[] = [];
        let totalBytes = slides.reduce((sum, slide) => sum + (slide.file?.size ?? 0), 0);
        for (const file of files) {
            const kind = getFileKind(file);
            if (!kind || !file.size) {
                errors.push(`${file.name}: use a JPEG, PNG, MP4, or MOV file.`);
                continue;
            }
            const maxBytes = kind === "video" ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
            if (file.size > maxBytes) {
                errors.push(`${file.name}: ${kind === "video" ? "videos" : "images"} can be up to ${kind === "video" ? "50" : "10"} MB each.`);
                continue;
            }
            if (totalBytes + file.size > MAX_TOTAL_BYTES) {
                errors.push(`${file.name}: the post cannot exceed 250 MB in total.`);
                continue;
            }
            const url = URL.createObjectURL(file);
            try {
                const metadata = await inspectMedia(url, kind);
                if (kind === "video" &&
                    (!Number.isFinite(metadata.duration) ||
                        !metadata.duration ||
                        metadata.duration > MAX_VIDEO_SECONDS + 0.1)) {
                    errors.push(`${file.name}: videos can be up to ${MAX_VIDEO_SECONDS} seconds.`);
                    URL.revokeObjectURL(url);
                    continue;
                }
                additions.push({
                    id: `${file.name}-${file.lastModified}-${crypto.randomUUID()}`,
                    kind,
                    url,
                    name: file.name,
                    file,
                    ...metadata,
                });
                totalBytes += file.size;
            }
            catch {
                URL.revokeObjectURL(url);
                errors.push(`${file.name}: its media metadata could not be read.`);
            }
        }
        if (additions.length) {
            setSlides((existing) => [...existing, ...additions].slice(0, MAX_SLIDES));
            setActive(0);
        }
        setUploadError(errors.join(" "));
        setUploadChecking(false);
        input.value = "";
    }
    function handleAvatar(event: ChangeEvent<HTMLInputElement>) {
        const file = event.target.files?.[0];
        if (!file)
            return;
        if (getFileKind(file) !== "image" || file.size > MAX_IMAGE_BYTES) {
            setUploadError("Profile image: use a JPEG or PNG image up to 10 MB.");
            event.target.value = "";
            return;
        }
        setUploadError("");
        if (avatar?.startsWith("blob:"))
            URL.revokeObjectURL(avatar);
        setAvatar(URL.createObjectURL(file));
        setAvatarFile(file);
        event.target.value = "";
    }
    function removeSlide(id: string) {
        const removedIndex = slides.findIndex((slide) => slide.id === id);
        setActive((index) => {
            if (slides.length === 1)
                return 0;
            if (index > removedIndex)
                return index - 1;
            return Math.min(index, slides.length - 2);
        });
        setSlides((existing) => {
            const target = existing.find((slide) => slide.id === id);
            if (target?.url.startsWith("blob:"))
                URL.revokeObjectURL(target.url);
            return existing.filter((slide) => slide.id !== id);
        });
    }
    function moveSlide(index: number, direction: number) {
        const target = index + direction;
        if (target < 0 || target >= slides.length)
            return;
        setSlides((existing) => {
            const next = [...existing];
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
        if (active === index)
            setActive(target);
        else if (active === target)
            setActive(index);
    }
    function moveSampleToFront(index: number) {
        if (!sampleMode || index <= 0 || index >= slides.length)
            return;
        setSlides((existing) => {
            const next = [...existing];
            const [selected] = next.splice(index, 1);
            next.unshift(selected);
            return next;
        });
        setActive(0);
    }
    function dropSlide(event: DragEvent<HTMLDivElement>, targetId: string) {
        event.preventDefault();
        if (!draggedId || draggedId === targetId)
            return;
        const sourceIndex = slides.findIndex((slide) => slide.id === draggedId);
        const targetIndex = slides.findIndex((slide) => slide.id === targetId);
        setSlides((existing) => {
            const sourceIndex = existing.findIndex((slide) => slide.id === draggedId);
            const targetIndex = existing.findIndex((slide) => slide.id === targetId);
            if (sourceIndex < 0 || targetIndex < 0)
                return existing;
            const next = [...existing];
            const [moved] = next.splice(sourceIndex, 1);
            next.splice(targetIndex, 0, moved);
            return next;
        });
        setActive((index) => {
            if (index === sourceIndex)
                return targetIndex;
            if (sourceIndex < index && index <= targetIndex)
                return index - 1;
            if (targetIndex <= index && index < sourceIndex)
                return index + 1;
            return index;
        });
        setDraggedId(null);
    }
    function resetEditor() {
        slides.forEach((slide) => {
            if (slide.url.startsWith("blob:"))
                URL.revokeObjectURL(slide.url);
        });
        if (avatar?.startsWith("blob:"))
            URL.revokeObjectURL(avatar);
        setSlides([]);
        setActive(0);
        setRatio("4 / 5");
        setUsername(DEFAULT_USERNAME);
        setLocation(DEFAULT_LOCATION);
        setCaption(DEFAULT_CAPTION);
        setLikes(DEFAULT_LIKES);
        setAvatar(DEFAULT_AVATAR);
        setAvatarFile(null);
        setCaptionOpen(false);
        setLiked(false);
        setReposted(false);
        setSaved(false);
        setUploadError("");
        setShareStatus("idle");
        setShareProgress("");
        setShareUrl("");
        setShareError("");
    }
    function clearFields() {
        if (avatar?.startsWith("blob:"))
            URL.revokeObjectURL(avatar);
        setUsername("");
        setLocation("");
        setCaption("");
        setLikes("");
        setAvatar(null);
        setAvatarFile(null);
        setCaptionOpen(false);
        if (avatarRef.current)
            avatarRef.current.value = "";
    }
    async function loadPreviewHistory() {
        if (!editorKey && !account)
            return;
        setHistoryLoading(true);
        setHistoryError("");
        try {
            const result = await fetchPreviewHistory(editorKey || undefined);
            setPreviewHistory(result.items ?? []);
        }
        catch (error) {
            setHistoryError(error instanceof Error
                ? error.message
                : "Saved previews could not be loaded.");
        }
        finally {
            setHistoryLoading(false);
        }
    }
    async function finishExistingSharePreview(previewId: string, paymentId: string) {
        setShareStatus("saving");
        setShareProgress("Creating your paid preview...");
        setShareError("");
        const slideSpecs = slides.map((slide) => ({
            name: slide.name,
            type: slide.file?.type || "",
            size: slide.file?.size || 0,
            kind: slide.kind,
            ...(slide.duration ? { duration: slide.duration } : {}),
            ...(slide.sourceRatio ? { sourceRatio: slide.sourceRatio } : {}),
        }));
        const avatarSpec = avatarFile
            ? {
                name: avatarFile.name,
                type: avatarFile.type,
                size: avatarFile.size,
                kind: "image",
            }
            : null;
        const response = await fetch(`/api/share/${previewId}/finalize`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                username,
                location,
                caption,
                likes,
                ratio,
                slides: slideSpecs,
                avatar: avatarSpec,
                paymentId,
                termsAccepted,
                termsVersion: TERMS_VERSION,
            }),
        });
        const result = await readApiResponse<{
            id: string;
        }>(response);
        if (!result.id)
            throw new Error("The preview link could not be created.");
        const url = `${window.location.origin}/preview/${result.id}`;
        setShareUrl(url);
        setShareStatus("ready");
        setShareProgress("");
        await navigator.clipboard.writeText(url).catch(() => undefined);
        const history = await fetchPreviewHistory().catch(() => null);
        if (history)
            setPreviewHistory(history.items ?? []);
        return url;
    }
    async function startGoogleSignIn(event: ReactMouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        setShareError("");
        try {
            await persistDraft();
            window.location.assign("/api/auth/google/start");
        }
        catch {
            setShareError("Your draft could not be saved in this browser. Free some browser storage and try again before signing in.");
        }
    }
    async function signOut() {
        await fetch("/api/auth/logout", { method: "POST" }).catch(() => undefined);
        setAccount(null);
        setPreviewHistory([]);
        setPaymentNotice(null);
    }
    async function copyHistoryUrl(item: PreviewHistoryItem) {
        const url = `${window.location.origin}${item.previewUrl}`;
        await navigator.clipboard.writeText(url);
        setCopiedHistoryId(item.id);
        window.setTimeout(() => setCopiedHistoryId((currentId) => currentId === item.id ? "" : currentId), 1800);
    }
    async function deletePreview(item: PreviewHistoryItem) {
        if (!window.confirm("Are you sure? This action cannot be undone."))
            return;
        setDeletingId(item.id);
        setHistoryError("");
        try {
            const response = await fetch(`/api/share/${item.id}`, {
                method: "DELETE",
                headers: { "x-editor-key": editorKey },
            });
            await readApiResponse<{
                ok: boolean;
            }>(response);
            setPreviewHistory((items) => items.filter((preview) => preview.id !== item.id));
        }
        catch (error) {
            setHistoryError(error instanceof Error
                ? error.message
                : "This preview could not be deleted.");
        }
        finally {
            setDeletingId("");
        }
    }
    async function restorePreview(item: PreviewHistoryItem) {
        setRestoringId(item.id);
        setHistoryError("");
        const createdUrls: string[] = [];
        try {
            const restoredSlides: UploadedSlide[] = [];
            for (const [index, slide] of item.slides.entries()) {
                const response = await fetch(slide.url);
                if (!response.ok)
                    throw new Error(`Slide ${index + 1} could not be restored.`);
                const blob = await response.blob();
                const type = blob.type || (slide.kind === "video" ? "video/mp4" : "image/jpeg");
                const file = new File([blob], slide.name || `slide-${index + 1}`, {
                    type,
                    lastModified: Date.now(),
                });
                const url = URL.createObjectURL(file);
                createdUrls.push(url);
                const metadata = await inspectMedia(url, slide.kind);
                restoredSlides.push({
                    id: `restored-${item.id}-${index}-${crypto.randomUUID()}`,
                    kind: slide.kind,
                    url,
                    name: file.name,
                    file,
                    ...metadata,
                });
            }
            let restoredAvatar: string | null = DEFAULT_AVATAR;
            let restoredAvatarFile: File | null = null;
            if (item.avatarUrl) {
                const response = await fetch(item.avatarUrl);
                if (!response.ok)
                    throw new Error("The profile image could not be restored.");
                const blob = await response.blob();
                restoredAvatarFile = new File([blob], "profile-image", {
                    type: blob.type || "image/png",
                    lastModified: Date.now(),
                });
                restoredAvatar = URL.createObjectURL(restoredAvatarFile);
                createdUrls.push(restoredAvatar);
            }
            slides.forEach((slide) => {
                if (slide.url.startsWith("blob:"))
                    URL.revokeObjectURL(slide.url);
            });
            if (avatar?.startsWith("blob:"))
                URL.revokeObjectURL(avatar);
            setSlides(restoredSlides);
            setActive(0);
            setUsername(item.username);
            setLocation(item.location);
            setCaption(item.caption);
            setLikes(item.likes);
            setRatio(item.ratio);
            setAvatar(restoredAvatar);
            setAvatarFile(restoredAvatarFile);
            setCaptionOpen(false);
            setLiked(false);
            setReposted(false);
            setSaved(false);
            setShareStatus("idle");
            setShareProgress("");
            setShareUrl("");
            setShareError("");
            document
                .getElementById("top")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        catch (error) {
            createdUrls.forEach((url) => URL.revokeObjectURL(url));
            setHistoryError(error instanceof Error
                ? error.message
                : "This preview could not be restored.");
        }
        finally {
            setRestoringId("");
        }
    }
    function changeTermsAcceptance(accepted: boolean) {
        setTermsAccepted(accepted);
        try {
            if (accepted)
                window.localStorage.setItem(TERMS_STORAGE, TERMS_VERSION);
            else
                window.localStorage.removeItem(TERMS_STORAGE);
        }
        catch {
            // The current page still retains the explicit choice.
        }
    }
    async function createSharePreview() {
        if (caption.length > MAX_CAPTION_CHARS) {
            setShareError(`Captions cannot exceed ${MAX_CAPTION_CHARS} characters.`);
            return;
        }
        if (!editorKey && !termsAccepted) {
            setShareError("Review and accept the Terms before exporting.");
            return;
        }
        const uploadedSlides = slides.filter((slide) => Boolean(slide.file));
        if (uploadedSlides.length !== slides.length) {
            setShareError("Upload your media before creating a preview link.");
            return;
        }
        let rememberedEditorKey = "";
        try {
            rememberedEditorKey =
                window.localStorage.getItem(EDITOR_KEY_STORAGE) ?? "";
        }
        catch {
            rememberedEditorKey = "";
        }
        const activeEditorKey = editorKey ||
            new URLSearchParams(window.location.search).get("edit") ||
            rememberedEditorKey;
        if (!activeEditorKey && !account) {
            try {
                await persistDraft();
                window.location.assign("/api/auth/google/start");
            }
            catch {
                setShareError("Your draft could not be saved in this browser. Free some browser storage and try again before signing in.");
            }
            return;
        }
        const assets: UploadAsset[] = uploadedSlides.map((slide, index) => describeAsset(slide.file as File, `slide-${index}`, slide.kind, slide.duration, slide.sourceRatio));
        if (!activeEditorKey) {
            try {
                const response = await fetch("/api/payments/readiness", { cache: "no-store" });
                const readiness = response.ok ? await response.json() as { available: boolean; mode: string | null } : { available: false, mode: null };
                setCheckoutReadiness(readiness);
                if (!readiness.available) {
                    setShareError("QR checkout is temporarily unavailable. Your draft is preserved; please try again later.");
                    return;
                }
            } catch {
                setShareError("QR checkout is temporarily unavailable. Your draft is preserved; please try again later.");
                return;
            }
        }
        if (avatarFile)
            assets.push(describeAsset(avatarFile, "avatar", "image"));
        const slideSpecs = assets
            .filter((asset) => asset.slot.startsWith("slide-"))
            .map(({ name, type, size, kind, duration, sourceRatio }) => ({
            name,
            type,
            size,
            kind,
            ...(duration ? { duration } : {}),
            ...(sourceRatio ? { sourceRatio } : {}),
        }));
        const avatarAsset = assets.find((asset) => asset.slot === "avatar");
        const avatarSpec = avatarAsset
            ? {
                name: avatarAsset.name,
                type: avatarAsset.type,
                size: avatarAsset.size,
                kind: avatarAsset.kind,
            }
            : null;
        setShareStatus("saving");
        setShareProgress("Preparing upload...");
        setShareError("");
        let previewId = "";
        let preserveUpload = false;
        try {
            const startResponse = await fetch("/api/share", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-editor-key": activeEditorKey,
                },
                body: JSON.stringify({ slides: slideSpecs, avatar: avatarSpec }),
            });
            const start = await readApiResponse<{
                id: string;
                chunkSize: number;
            }>(startResponse);
            if (!start.id || !start.chunkSize)
                throw new Error("The upload could not be started.");
            previewId = start.id;
            const totalChunks = assets.reduce((sum, asset) => sum + Math.ceil(asset.size / start.chunkSize), 0);
            let completedChunks = 0;
            for (const asset of assets) {
                for (let offset = 0, part = 0; offset < asset.size; offset += start.chunkSize, part += 1) {
                    setShareProgress(`Uploading ${completedChunks + 1} of ${totalChunks}...`);
                    const chunk = asset.file.slice(offset, Math.min(offset + start.chunkSize, asset.size));
                    const uploadResponse = await fetch(`/api/share/${previewId}/upload/${asset.slot}?part=${part}`, {
                        method: "PUT",
                        headers: {
                            "Content-Type": "application/octet-stream",
                            "x-editor-key": activeEditorKey,
                        },
                        body: chunk,
                    });
                    await readApiResponse<{
                        ok: boolean;
                    }>(uploadResponse);
                    completedChunks += 1;
                }
            }
            if (!activeEditorKey) {
                preserveUpload = true;
                setShareProgress("Opening QR checkout...");
                await persistDraft();
                const paymentResponse = await fetch("/api/payments/session", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        shareUploadId: previewId,
                        idempotencyKey: crypto.randomUUID(),
                    }),
                });
                const checkout = await readApiResponse<{
                    checkoutUrl: string;
                }>(paymentResponse);
                if (!checkout.checkoutUrl)
                    throw new Error("The QR payment provider did not return a checkout URL.");
                window.location.assign(checkout.checkoutUrl);
                return;
            }
            setShareProgress("Creating your preview...");
            const finalizeResponse = await fetch(`/api/share/${previewId}/finalize`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-editor-key": activeEditorKey,
                },
                body: JSON.stringify({
                    username,
                    location,
                    caption,
                    likes,
                    ratio,
                    slides: slideSpecs,
                    avatar: avatarSpec,
                    termsAccepted,
                    termsVersion: TERMS_VERSION,
                }),
            });
            const result = await readApiResponse<{
                id: string;
            }>(finalizeResponse);
            if (!result.id)
                throw new Error("The preview link could not be created.");
            const url = `${window.location.origin}/preview/${result.id}`;
            setShareUrl(url);
            setShareStatus("ready");
            setShareProgress("");
            await navigator.clipboard.writeText(url).catch(() => undefined);
            const history = await fetchPreviewHistory(activeEditorKey).catch(() => null);
            if (history)
                setPreviewHistory(history.items ?? []);
            if (!activeEditorKey)
                await refreshAccount().catch(() => null);
        }
        catch (error) {
            if (previewId && !preserveUpload) {
                await fetch(`/api/share/${previewId}/cleanup`, {
                    method: "DELETE",
                    headers: { "x-editor-key": activeEditorKey },
                }).catch(() => undefined);
            }
            setShareStatus("idle");
            setShareProgress("");
            setShareError(error instanceof Error
                ? error.message
                : "Preview could not be created.");
            if (!activeEditorKey)
                await refreshAccount().catch(() => null);
        }
    }
    async function copyShareUrl() {
        if (!shareUrl)
            return;
        await navigator.clipboard.writeText(shareUrl);
    }
    function onPointerDown(event: PointerEvent<HTMLDivElement>) {
        pointerStart.current = event.clientX;
        suppressZoom.current = false;
    }
    function onPointerUp(event: PointerEvent<HTMLDivElement>) {
        if (pointerStart.current === null)
            return;
        const distance = event.clientX - pointerStart.current;
        suppressZoom.current = Math.abs(distance) > 8;
        if (Math.abs(distance) > 45)
            go(distance < 0 ? 1 : -1);
        pointerStart.current = null;
        window.setTimeout(() => {
            suppressZoom.current = false;
        }, 0);
    }
    function openImageZoom(slide: Slide) {
        if (suppressZoom.current || slide.kind !== "image")
            return;
        setZoomLevel(1);
        setZoomedSlide(slide);
    }
    function closeImageZoom() {
        setZoomedSlide(null);
        setZoomLevel(1);
    }
    function changeZoom(amount: number) {
        setZoomLevel((level) => Math.min(Math.max(level + amount, 1), 4));
    }
    return (<main className={presentMode ? "app app--present" : "app"}>
      <header className="topbar">
        <a className="brand" href={presentMode ? "/" : "#top"} aria-label="RupaPreview home">
          <img className="brand-logo" src="/rupaworks-logo.png" alt="Rupaworks"/>
          <span className="brand-divider" aria-hidden="true"/>
          <span className="brand-product">
            <span>Rupa</span>
            <strong>Preview</strong>
          </span>
        </a>
        <div className="topbar__actions">
          {presentMode ? (<a className="public-create" href="/">
              Make your own preview <span aria-hidden="true">↗</span>
            </a>) : (<>
              {!editorKey &&
                (accountLoading ? (<span className="account-loading">Loading account…</span>) : account ? (<details className="account-control">
                    <summary>
                      <span className="account-control__label">Account</span>
                      <strong>{account.name.split(" ")[0]}</strong>
                    </summary>
                    <div className="account-control__menu">
                      <span>Signed in with Google</span>
                      <strong>{account.name}</strong>
                      <p>{account.email}</p>
                      <button type="button" onClick={signOut}>
                        Sign out
                      </button>
                    </div>
                  </details>) : (<a className="google-signin" href="/api/auth/google/start" onClick={startGoogleSignIn}>
                    Sign in with Google <span aria-hidden="true">↗</span>
                  </a>))}
              {editorKey && <span className="privacy-note">Private editor</span>}
              <details className="header-info" name="preview-header-info">
                <summary>Cheap &amp; Easy</summary>
                <div className="header-info__card">
                  <p>One paid export creates one preview link for {linkPriceLabel} — no subscription or expiring credits.</p>
                  <p>Your link stays active for 14 days. Keep your original files; this is not permanent storage.</p>
                  <p>Unlisted does not mean private: anyone with the URL can open it.</p>
                  <p>Only upload content you own or have permission to use.</p>
                  <p>Verified payment with a technical export failure? Email <a href="mailto:hallo@rupaworks.com">hallo@rupaworks.com</a> for review and refund handling.</p>
                </div>
              </details>
              <details className="header-info" name="preview-header-info">
                <summary>Why Sign In?</summary>
                <div className="header-info__card">
                  <p>Your Google account keeps this draft attached to you during checkout, verifies who owns the purchase, and gives you one place to recover recent links. Drafting and previewing stay free.</p>
                </div>
              </details>
              <a className="product-link" href="/sample" target="_blank" rel="noreferrer">
                Sample Post <span aria-hidden="true">↗</span>
              </a>
            </>)}
        </div>
      </header>

      {!editorKey && paymentNotice && (<div className={`payment-notice payment-notice--${paymentNotice.tone}`} role="status">
          {paymentNotice.message}
        </div>)}
      <section className="workspace" id="top">
        {presentMode && (<aside className="public-intro" aria-label={sampleMode ? "About this permanent sample" : "About this shared preview"}>
            <div>
              <span className="public-intro__kicker">
                {sampleMode ? "PERMANENT RUPAPREVIEW SAMPLE" : "SHARED VIA RUPAPREVIEW"}
              </span>
              <h1>
                {sampleMode ? (<>Five crops.<br />One story.</>) : (<>The post.<br />Nothing else.</>)}
              </h1>
              <p>
                {sampleMode
                ? "Move any image to position 01. Its ratio sets the crop for the whole carousel—just like your own post."
                : publicTip}
              </p>
            </div>
            <dl className="public-intro__meta">
              <div>
                <dt>PROFILE</dt>
                <dd>{username || "username"}</dd>
              </div>
              <div>
                <dt>MEDIA</dt>
                <dd>{slides.length} slide{slides.length === 1 ? "" : "s"}</dd>
              </div>
              <div>
                <dt>{sampleMode ? "CURRENT CROP" : "ACCESS"}</dt>
                <dd>{sampleMode ? currentRatio.label : "Unlisted · Login-free"}</dd>
              </div>
            </dl>
            {sampleMode && (<section className="sample-order" aria-labelledby="sample-order-title">
                <div className="sample-order__heading">
                  <h2 id="sample-order-title">Change the slide order</h2>
                  <span>{currentRatio.dimensions}</span>
                </div>
                <div className="sample-order__list">
                  {slides.map((slide, index) => (<div className={`sample-order__item ${index === 0 ? "sample-order__item--first" : ""}`} draggable key={slide.id} onDragStart={() => setDraggedId(slide.id)} onDragOver={(event) => event.preventDefault()} onDrop={(event) => dropSlide(event, slide.id)}>
                      <button type="button" className="sample-order__preview" onClick={() => setActive(index)} aria-label={`Preview ${slide.name}`}>
                        <span>{String(index + 1).padStart(2, "0")}</span>
                        <img src={slide.url} alt="" draggable={false}/>
                      </button>
                      <button type="button" className="sample-order__move" onClick={() => moveSampleToFront(index)} disabled={index === 0} aria-label={index === 0 ? `${slide.name} sets the crop` : `Move ${slide.name} to position 1`}>
                        {index === 0 ? "Sets the crop" : "Move to 01"}
                      </button>
                    </div>))}
                </div>
                <p>Drag to reorder, or use Move to 01. Nothing here expires.</p>
              </section>)}
          </aside>)}
        <aside className="editor" aria-label="Post preview editor">
          <div className="editor__heading">
            <div>
              <span className="section-kicker">SETUP</span>
              <h1>
                <span className="heading-muted">Build your</span>{" "}
                <span className="heading-ink">post preview</span>
              </h1>
            </div>
            <button className="text-button" onClick={resetEditor}>
              Clear all section
            </button>
          </div>

          <section className="control-section">
            <div className="control-title">
              <span>01</span>
              <div>
                <h2>Post assets</h2>
                <p>Upload up to 20 images or short videos in any order.</p>
              </div>
            </div>
            <input id="carousel-upload" ref={uploadRef} className="visually-hidden" type="file" accept=".jpg,.jpeg,.png,.mp4,.mov,image/jpeg,image/png,video/mp4,video/quicktime" multiple onChange={handleFiles}/>
            <label className="dropzone" htmlFor="carousel-upload" role="button" tabIndex={0} aria-busy={uploadChecking} onKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== " ")
                return;
            event.preventDefault();
            uploadRef.current?.click();
        }}>
              <span className="dropzone__plus">+</span>
              <strong>
                {uploadChecking
            ? "Checking media..."
            : slides.length
                ? "Add more media"
                : "Upload media"}
              </strong>
              <small>JPEG, PNG, MP4 or MOV</small>
            </label>
            <ul className="upload-requirements">
              <li>Images: JPEG or PNG, up to 10 MB each.</li>
              <li>Videos: MP4 or MOV, up to 60 seconds and 50 MB each.</li>
              <li>Total: up to 250 MB per post.</li>
            </ul>
            <div className="crop-rule-note">
              <strong>Your first file sets the crop.</strong>
              <p>
                Every slide in a multi-slide post uses that same crop, just like
                Instagram. To change it, move a different file to position 01 or
                replace the first file.
              </p>
            </div>
            {uploadError && (<p className="upload-error" role="alert">
                {uploadError}
              </p>)}

            <div className="slide-list" aria-label="Slide order">
              {slides.map((slide, index) => (<div className={`slide-row ${active === index ? "slide-row--active" : ""}`} draggable key={slide.id} onDragStart={() => setDraggedId(slide.id)} onDragOver={(event) => event.preventDefault()} onDrop={(event) => dropSlide(event, slide.id)}>
                  <button className="slide-row__preview" onClick={() => setActive(index)} aria-label={`Preview slide ${index + 1}`}>
                    <span className="slide-row__number">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="slide-row__thumb">
                      <SlideVisual slide={slide} thumbnail/>
                    </span>
                    <span className="slide-row__name">{slide.name}</span>
                  </button>
                  <div className="slide-row__actions">
                    <button onClick={() => moveSlide(index, -1)} disabled={index === 0} aria-label="Move slide left">
                      ←
                    </button>
                    <button onClick={() => moveSlide(index, 1)} disabled={index === slides.length - 1} aria-label="Move slide right">
                      →
                    </button>
                    <button className="danger" onClick={() => removeSlide(slide.id)} aria-label="Remove slide">
                      ×
                    </button>
                  </div>
                </div>))}
            </div>
          </section>

          <section className="control-section control-section--crop" aria-labelledby="feed-crop-title">
            <div className="control-title control-title--static">
              <div>
                <h2 id="feed-crop-title">Feed crop</h2>
                <p>Based on your first file’s dimensions.</p>
              </div>
            </div>
            <div className={slides.length ? "crop-status" : "crop-status crop-status--empty"} aria-live="polite">
              <span className="crop-status__shape" aria-hidden="true">
                <i style={slides.length
            ? { aspectRatio: currentRatio.value }
            : undefined}/>
              </span>
              <div className="crop-status__copy">
                {slides.length ? (<>
                    <strong>{currentRatio.label}</strong>
                    <span>{currentRatio.dimensions}</span>
                    <small>From 01 · {slides[0].name}</small>
                  </>) : (<>
                    <strong>No crop set yet</strong>
                    <span>Upload your first file to set it automatically.</span>
                  </>)}
              </div>
              {slides.length > 0 && (<span className="crop-status__tag">
                  {slides.length > 1
                ? `All ${slides.length} slides`
                : "Your post"}
                </span>)}
            </div>
          </section>

          <section className="control-section">
            <div className="control-title control-title--with-action">
              <span>02</span>
              <div>
                <h2>Post details</h2>
                <p>Choose what you want people to see.</p>
              </div>
              <button type="button" className="text-button" onClick={clearFields}>
                Clear fields
              </button>
            </div>
            <div className="field-grid">
              <label>
                <span>Profile image</span>
                <input ref={avatarRef} className="visually-hidden" type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" onChange={handleAvatar}/>
                <button className="input-like" onClick={() => avatarRef.current?.click()}>
                  Upload to change avatar
                </button>
              </label>
              <label>
                <span>Username</span>
                <input value={username} onChange={(event) => setUsername(event.target.value)}/>
              </label>
              <label>
                <span>Likes</span>
                <input value={likes} onChange={(event) => setLikes(event.target.value)}/>
              </label>
              <label>
                <span>Location</span>
                <input value={location} onChange={(event) => setLocation(event.target.value)}/>
              </label>
            </div>
            <label className="textarea-field">
              <span>Caption</span>
              <textarea rows={6} value={caption} maxLength={MAX_CAPTION_CHARS} aria-describedby="caption-rules" onChange={(event) => setCaption(event.target.value)}/>
              <span className="caption-meta caption-meta--end" id="caption-rules">
                <span>
                  {caption.length.toLocaleString("en-US")}/
                  {MAX_CAPTION_CHARS.toLocaleString("en-US")} characters
                </span>
              </span>
            </label>
          </section>
        </aside>

        <aside className="preview-column" aria-label="Live preview and sharing">
          <section className="stage" aria-label="Instagram post preview">
            <div className="stage__heading">
              <div>
                <span className="section-kicker">LIVE PREVIEW</span>
                <h2>
                  <span className="heading-ink">See your post</span>{" "}
                  <span className="heading-muted">as people will see it.</span>
                </h2>
              </div>
              {slides.length > 1 && (<span className="slide-count">
                  {active + 1} / {slides.length}
                </span>)}
            </div>

            <div className="post" tabIndex={0} onKeyDown={(event) => {
            if (event.key === "ArrowLeft")
                go(-1);
            if (event.key === "ArrowRight")
                go(1);
        }}>
              <div className="post__header">
                <button className="avatar" disabled={readOnly} onClick={() => avatarRef.current?.click()} aria-label={readOnly ? "Profile image" : "Change profile image"}>
                  <span className="avatar__media">
                    {avatar ? (<img src={avatar} alt={`${username || DEFAULT_USERNAME} profile`}/>) : (<ProfilePlaceholder />)}
                  </span>
                </button>
                <div className="post__identity">
                  <strong>{username || "username"}</strong>
                  <span>{location}</span>
                </div>
                <button className="icon-button" aria-label="More options" onClick={() => showSimulatorNotice("This is an interactive post preview.")}>
                  <Icon name="more"/>
                </button>
              </div>

              <div className={`post__media ${current ? "" : "post__media--empty"}`} style={{ aspectRatio: ratio }} onPointerDown={onPointerDown} onPointerUp={onPointerUp} onPointerCancel={() => {
            pointerStart.current = null;
            suppressZoom.current = false;
        }}>
                {current?.kind === "image" && (<button type="button" className="zoom-trigger" onClick={() => openImageZoom(current)} aria-label={`Zoom ${current.name}`}>
                    <SlideVisual slide={current}/>
                    <span className="zoom-hint">
                      <ZoomIcon />
                    </span>
                  </button>)}
                {current?.kind === "video" && <SlideVisual slide={current}/>}
                {!current && (<label className="empty-media" htmlFor="carousel-upload" role="button" tabIndex={0} onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ")
                    return;
                event.preventDefault();
                uploadRef.current?.click();
            }}>
                    <span>+</span>
                    <strong>Upload media</strong>
                  </label>)}
                {slides.length > 1 && active > 0 && (<button className="nav nav--left" onClick={() => go(-1)} aria-label="Previous slide">
                    ‹
                  </button>)}
                {slides.length > 1 && active < slides.length - 1 && (<button className="nav nav--right" onClick={() => go(1)} aria-label="Next slide">
                    ›
                  </button>)}
                {slides.length > 1 && (<span className="media-counter">
                    {active + 1}/{slides.length}
                  </span>)}
              </div>

              <div className="post__body">
                <div className="post__actions">
                  <div>
                    <button className={`icon-button icon-button--with-count ${liked ? "icon-button--liked" : ""}`} aria-label={liked ? "Unlike" : "Like"} aria-pressed={liked} onClick={() => setLiked((value) => !value)}>
                      <Icon name="heart" filled={liked}/>
                      <span className="like-count">{displayedLikes}</span>
                    </button>
                    <button className="icon-button" aria-label="Comment" onClick={() => showSimulatorNotice("Comments are illustrative in this preview.")}>
                      <Icon name="comment"/>
                    </button>
                    <button className="icon-button icon-button--with-count" aria-label={reposted ? "Undo repost" : "Repost"} aria-pressed={reposted} onClick={() => setReposted((value) => !value)}>
                      <Icon name="repost"/>
                      {reposted && <span>1</span>}
                    </button>
                    <button className="icon-button" aria-label="Share" onClick={shareCurrentPage}>
                      <Icon name="share"/>
                    </button>
                  </div>
                  <div className="dots" aria-label={slides.length > 1
            ? `Slide ${active + 1} of ${slides.length}`
            : undefined} aria-hidden={slides.length <= 1}>
                    {slides.length > 1 &&
            slides.map((slide, index) => (<button key={slide.id} className={active === index ? "dot dot--active" : "dot"} onClick={() => setActive(index)} aria-label={`Go to slide ${index + 1}`}/>))}
                  </div>
                  <button className={`icon-button ${saved ? "icon-button--saved" : ""}`} aria-label="Save" aria-pressed={saved} onClick={() => {
            setSaved((value) => !value);
            showSimulatorNotice("Saving is illustrative in this preview.");
        }}>
                    <Icon name="save" filled={saved}/>
                  </button>
                </div>
                {caption && (<p className="caption">
                    <strong>{username || "username"}</strong> {truncatedCaption}
                  </p>)}
                {caption.length > CAPTION_PREVIEW_CHARS && (<button className="more-caption" onClick={() => setCaptionOpen((value) => !value)}>
                    {captionOpen ? "less" : "more"}
                  </button>)}
                <span className="timestamp">2 hours ago</span>
                {simulatorNotice && (<p className="simulator-notice" role="status">
                    {simulatorNotice}
                  </p>)}
              </div>
            </div>

            <div className="stage__tip">
              <span>TEST IT</span>
              <p>{editorTip}</p>
            </div>
          </section>

          <div className="preview-actions">
            <section className="control-section">
              <div className="control-title">
                <span>03</span>
                <div>
                  <h2>Preview link</h2>
                  <p>
                    Create an unlisted, login-free link that expires after 14
                    days.
                  </p>
                </div>
              </div>
              <div className="publish-facts" aria-label="Preview link details">
                <span>14 days</span>
                <span>Unlisted</span>
                <span>No viewer login</span>
              </div>
              {!editorKey && checkoutReadiness && (!checkoutReadiness.available || checkoutReadiness.mode === "sandbox") && (<p className="preview-publish-hint" role="status">
                  {checkoutReadiness.available
                    ? "Sandbox checkout — no real payment. Link export is being verified."
                    : "QR checkout is temporarily unavailable. You can keep editing and your draft stays saved."}
                </p>)}
              {!editorKey && (<label className="export-consent">
                  <input type="checkbox" checked={termsAccepted} onChange={(event) => changeTermsAcceptance(event.target.checked)}/>
                  <span>I agree to the <a href="/terms" target="_blank" rel="noreferrer">Terms</a>, including 14-day storage and the refund policy.</span>
                </label>)}
              <button type="button" className="preview-publish-action" onClick={createSharePreview} disabled={!slides.length || hasDemoSlides ||
            uploadChecking ||
            (!editorKey && !termsAccepted) ||
            shareStatus === "saving"} aria-busy={shareStatus === "saving"}>
                <span>
                  {shareStatus === "saving"
            ? shareProgress || "Creating preview..."
            : !editorKey && !account
                ? "SIGN IN TO EXPORT"
                : shareStatus === "ready"
                    ? `PAY ${linkPriceLabel.toUpperCase()} & EXPORT UPDATED LINK`
                    : !editorKey
                        ? `PAY ${linkPriceLabel.toUpperCase()} & EXPORT LINK`
                        : "CREATE LINK"}
                </span>
                <strong aria-hidden="true">↗</strong>
              </button>
              <a className="sample-link" href="/sample" target="_blank" rel="noreferrer">
                <span>Not ready to export yours?</span>
                <strong>SEE THE PREMADE SAMPLE ↗</strong>
              </a>
              {(hasDemoSlides || editorKey) && (<p className="preview-publish-hint">
                {hasDemoSlides ? "Clear the sample and upload your own media to export." : "Double-check your post. Your preview link will be valid for 14 days."}
              </p>)}
              {shareError && (<p className="preview-publish-message preview-publish-message--error" role="alert">
                  {shareError}
                </p>)}
              {shareUrl && (<div className="preview-publish-result" aria-live="polite">
                  <div>
                    <span>PREVIEW LINK READY</span>
                    <p>{shareUrl}</p>
                  </div>
                  <button onClick={copyShareUrl}>Copy link</button>
                </div>)}
            </section>

            <section className="control-section control-section--last">
              <div className="control-title">
                <span>04</span>
                <div>
                  <h2>Recent links</h2>
                  <p>Manage your last 7 preview links.</p>
                </div>
              </div>
              <div className="preview-history">
                <div className="preview-history__heading preview-history__heading--refresh">
                  <button type="button" onClick={loadPreviewHistory} disabled={historyLoading || (!editorKey && !account)}>
                    {historyLoading ? "Loading..." : "Refresh"}
                  </button>
                </div>
                {historyError && (<p className="preview-history__error" role="alert">
                    {historyError}
                  </p>)}
                {!historyLoading &&
            !historyError &&
            previewHistory.length === 0 && (<p className="preview-history__empty">
                      {!editorKey && !account
                ? "Sign in to see the preview links attached to your account."
                : "No preview links yet."}
                    </p>)}
                <div className="preview-history__list">
                  {previewHistory.map((item) => {
            const firstSlide = item.slides[0];
            return (<article className="preview-history__item" key={item.id}>
                        <div className="preview-history__thumb">
                          {firstSlide?.kind === "video" ? (<video src={firstSlide.url} muted playsInline preload="metadata" aria-label="First slide preview"/>) : (firstSlide && (<img src={firstSlide.url} alt="First slide preview"/>))}
                        </div>
                        <div className="preview-history__details">
                          <div className="preview-history__meta">
                            <strong>
                              {item.slides.length} slide
                              {item.slides.length === 1 ? "" : "s"}
                            </strong>
                            <span>{formatPreviewDate(item.createdAt)}</span>
                          </div>
                          <p>{item.caption || "No caption."}</p>
                          <small>
                            Expires {formatPreviewDate(item.expiresAt)}
                          </small>
                        </div>
                        <div className="preview-history__actions">
                          <button type="button" className="preview-history__restore" onClick={() => restorePreview(item)} disabled={Boolean(restoringId || deletingId)}>
                            {restoringId === item.id
                    ? "Restoring..."
                    : "Restore draft"}
                          </button>
                          <a href={item.previewUrl} target="_blank" rel="noreferrer">
                            Open preview
                          </a>
                          <button type="button" onClick={() => copyHistoryUrl(item)}>
                            {copiedHistoryId === item.id
                    ? "Copied!"
                    : "Copy link"}
                          </button>
                          <button type="button" onClick={() => deletePreview(item)} disabled={Boolean(deletingId || restoringId)}>
                            {deletingId === item.id ? "Deleting..." : "Delete"}
                          </button>
                        </div>
                      </article>);
        })}
                </div>
              </div>
            </section>
          </div>
        </aside>
      </section>

      {!presentMode && <SiteSupport />}

      {presentMode && (<footer className="public-footer">
          <span>RupaPreview by Rupaworks. © 2026 PT RUPAWORKS.</span>
          <span>
            {sampleMode
                ? "Permanent sample · No payment or sign-in required."
                : "This unlisted preview expires 14 days after creation."}
          </span>
          <span className="public-footer__links">
            <a href="/terms">Terms &amp; Privacy</a>
            <a href="https://seller.rupaworks.com/" target="_blank" rel="noreferrer">
              RupaSeller ↗
            </a>
          </span>
        </footer>)}

      {zoomedSlide && (<div className="image-zoom" role="dialog" aria-modal="true" aria-label={`Zoomed view of ${zoomedSlide.name}`}>
          <div className="image-zoom__toolbar">
            <button type="button" onClick={() => changeZoom(-0.5)} disabled={zoomLevel <= 1} aria-label="Zoom out">
              −
            </button>
            <span>{Math.round(zoomLevel * 100)}%</span>
            <button type="button" onClick={() => changeZoom(0.5)} disabled={zoomLevel >= 4} aria-label="Zoom in">
              +
            </button>
            <button type="button" className="image-zoom__close" onClick={closeImageZoom} aria-label="Close zoom">
              Close
            </button>
          </div>
          <div className="image-zoom__viewport">
            <img src={zoomedSlide.url} alt={zoomedSlide.name} draggable={false} style={{ width: `${zoomLevel * 100}%` }} onClick={() => setZoomLevel((level) => level >= 4 ? 1 : Math.min(level + 0.5, 4))}/>
          </div>
        </div>)}
    </main>);
}

